import React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface TableRow {
  id: string;
  label: string;
  type?: 'text' | 'number' | 'currency';
}

interface DualColumnTableProps {
  rows: TableRow[];
  column1Label: string;
  column2Label: string;
  value: {
    column1: Record<string, any>;
    column2: Record<string, any>;
  };
  onChange: (value: { column1: Record<string, any>; column2: Record<string, any> }) => void;
  className?: string;
}

export function DualColumnTable({
  rows,
  column1Label,
  column2Label,
  value,
  onChange,
  className,
}: DualColumnTableProps) {
  const col1 = value?.column1 || {};
  const col2 = value?.column2 || {};

  const handleChange = (
    column: 'column1' | 'column2',
    rowId: string,
    newValue: any
  ) => {
    onChange({
      ...value,
      [column]: {
        ...value[column],
        [rowId]: newValue,
      },
    });
  };

  const renderInput = (
    row: TableRow,
    column: 'column1' | 'column2',
    currentValue: any
  ) => {
    const inputType = row.type || 'text';
    
    if (inputType === 'currency') {
      return (
        <div className="relative">
          <Input
            type="number"
            value={currentValue || ''}
            onChange={(e) =>
              handleChange(column, row.id, e.target.value ? parseFloat(e.target.value) : null)
            }
            className="pr-8 h-9"
            placeholder="0,00"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            €
          </span>
        </div>
      );
    }

    return (
      <Input
        type={inputType}
        value={currentValue || ''}
        onChange={(e) =>
          handleChange(
            column,
            row.id,
            inputType === 'number' && e.target.value
              ? parseFloat(e.target.value)
              : e.target.value
          )
        }
        className="h-9"
      />
    );
  };

  return (
    <div className={cn("w-full", className)}>
      {/* Header */}
      <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 mb-2">
        <div /> {/* Empty cell for row labels */}
        <Label className="text-center font-semibold text-primary">
          {column1Label}
        </Label>
        <Label className="text-center font-semibold text-primary">
          {column2Label}
        </Label>
      </div>

      {/* Rows */}
      <div className="space-y-2">
        {rows.map((row) => (
          <div
            key={row.id}
            className="grid grid-cols-[1fr_1fr_1fr] gap-2 items-center"
          >
            <Label className="text-sm text-muted-foreground truncate">
              {row.label}
            </Label>
            {renderInput(row, 'column1', col1[row.id])}
            {renderInput(row, 'column2', col2[row.id])}
          </div>
        ))}
      </div>
    </div>
  );
}
